<script setup>
import { ref, computed, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { router, menuRoutes, menuRouteEnum } from '@/router'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'
import { useNotifications } from '@/composables/notifications'

import DockablePanel from '@/components/shared/dockable-panel/DockablePanel.vue'
import DockablePanelHeader from '@/components/shared/dockable-panel/DockablePanelHeader.vue'
import NotificationListItem from '@/components/NotificationListItem.vue'

const PANEL_NOTIFICATIONS_COUNT = 50

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { markAllAsReadAsync, openNotificationAsync, toggleReadAsync, snoozeAsync, hasTarget, loadLatestNotificationsAsync } = useNotifications()

const notifications = ref([])
const body = ref(null)

const caption = computed(() => `${i18n.t('common.unread')}: ${webSiteStore.notificationsCount}`)

const loadAsync = async() => {
  notifications.value = await loadLatestNotificationsAsync({
    take: PANEL_NOTIFICATIONS_COUNT,
    unreadOnly: webSiteStore.notificationPanelUnreadOnly,
  })
}

const reloadAndHighlightNewestAsync = async() => {
  const previousIds = new Set(notifications.value.map(item => item.id))
  await loadAsync()
  const [newest] = notifications.value
  if (newest && !previousIds.has(newest.id)) await highlightRowAsync(newest.id, body)
}

const whileShown = (callbackAsync) => () => webSiteStore.showNotifications && callbackAsync()

watch(() => [webSiteStore.showNotifications, webSiteStore.notificationPanelUnreadOnly], whileShown(loadAsync), { immediate: true })
watch(() => webSiteStore.notificationsChangedAt, whileShown(loadAsync))
watch(() => webSiteStore.notificationsArrivedAt, whileShown(reloadAndHighlightNewestAsync))

const onNotificationClick = async(notification) => {
  if (isMobile.value && hasTarget(notification)) webSiteStore.showNotifications = false
  await openNotificationAsync(notification)
}

const onNotificationSnooze = (notification, until) => snoozeAsync([notification.id], until)

const navigateToNotifications = () => {
  webSiteStore.showNotifications = false
  router.push(menuRoutes[menuRouteEnum.NOTIFICATIONS])
}
</script>

<template>
  <dockable-panel
    v-model="webSiteStore.showNotifications"
    v-model:width="webSiteStore.notificationPanelWidth"
    v-model:bounds="webSiteStore.notificationWindowBounds"
    :docked="webSiteStore.notificationPanelDocked"
  >
    <template #default="{ floating, move, close }">
      <div class="column no-wrap full-height">
        <dockable-panel-header
          v-model:docked="webSiteStore.notificationPanelDocked"
          icon="mdi-bell-outline"
          :title="i18n.t('menu.notifications')"
          :caption="caption"
          :floating="floating"
          @move="move"
          @close="close"
        >
          <template #actions>
            <q-btn
              flat
              round
              dense
              icon="mdi-email-alert-outline"
              :color="webSiteStore.notificationPanelUnreadOnly ? 'primary' : undefined"
              @click="webSiteStore.notificationPanelUnreadOnly = !webSiteStore.notificationPanelUnreadOnly"
            >
              <q-tooltip>{{ i18n.t('common.unread') }}</q-tooltip>
            </q-btn>
            <q-btn
              flat
              round
              dense
              icon="mdi-check-all"
              :disable="webSiteStore.notificationsCount === 0"
              @click="markAllAsReadAsync"
            >
              <q-tooltip>{{ i18n.t('common.markAllAsRead') }}</q-tooltip>
            </q-btn>
          </template>
        </dockable-panel-header>
        <q-scroll-area class="col">
          <q-list v-if="notifications.length" ref="body" class="q-pa-xs">
            <notification-list-item
              v-for="notification in notifications"
              :key="notification.id"
              :item="notification"
              :class="{ 'row-highlight': notification.id === highlightedRowId }"
              snoozable
              @click="onNotificationClick"
              @toggle-read="toggleReadAsync"
              @snooze="onNotificationSnooze"
            />
          </q-list>
          <div v-else class="column flex-center q-pa-lg text-center text-grey-7">
            <q-icon
              name="mdi-bell-check-outline"
              color="primary"
              size="xl"
              class="q-mb-md"
            />
            <div>{{ i18n.t(webSiteStore.notificationPanelUnreadOnly ? 'common.noUnreadNotification' : 'common.noNotification') }}</div>
          </div>
        </q-scroll-area>
        <q-separator />
        <q-btn
          flat
          no-caps
          color="primary"
          class="full-width q-py-sm"
          icon-right="mdi-chevron-right"
          :label="i18n.t('common.seeAll')"
          @click="navigateToNotifications"
        />
      </div>
    </template>
  </dockable-panel>
</template>
